import { v4 as uuid } from 'uuid'

import type { RentalsApiHeadersContext } from '#shared/types/rentals-api-headers'

/**
 * Builds the standard header set for rentals API requests.
 *
 * @param ctx Header values for the current request context.
 */
export function buildRentalsApiHeaders(ctx: RentalsApiHeadersContext): Record<string, string> {
  const headers: Record<string, string> = {
    'RVZ-version': ctx.rentalsApiVersion,
    'RVZ-Correlation-Id': ctx.correlationId ?? uuid(),
    'Platform': ctx.platform ?? 'web',
  }

  if (ctx.authorizationToken) {
    headers.Authorization = `Bearer ${ctx.authorizationToken}`
  }

  if (ctx.userAgent) {
    headers['User-Agent'] = ctx.userAgent
  }

  if (ctx.cfCountry) {
    headers['RVZ-ipcountry'] = ctx.cfCountry
  }

  if (ctx.cfRegion) {
    headers['RVZ-region'] = ctx.cfRegion
  }

  if (ctx.cfCity) {
    headers['RVZ-ipcity'] = ctx.cfCity
  }

  if (ctx.anonymousId) {
    headers['RVZ-Anonymous-Id'] = ctx.anonymousId
  }

  if (ctx.cookie) {
    headers.cookie = ctx.cookie
  }

  return headers
}

/**
 * Merges rentals API headers into an ofetch request options object.
 *
 * @param options Fetch options to augment.
 * @param ctx Header values for the current request context.
 */
export function applyRentalsApiHeadersToFetchOptions(
  options: {
    headers?: HeadersInit
    credentials?: RequestCredentials
  },
  ctx: RentalsApiHeadersContext,
): void {
  const headers = new Headers(options.headers)

  for (const [key, value] of Object.entries(buildRentalsApiHeaders(ctx))) {
    headers.set(key, value)
  }

  options.headers = headers

  if (ctx.includeCredentials) {
    options.credentials = 'include'
  }
}
